import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { API } from 'src/environments/environment';
import { BanrisulApiAmbiente } from 'src/app/constants/banrisul-financeiro.constants';
import { AjaxService } from './ajax.service';
import { ObservacaoContaReceberPayload } from './fin-conta-receber-lista.service';
import { UtilService } from './util.service';

export interface BanrisulApiConfigView {
  configurado: boolean;
  descricao: string;
  agencia: string;
  digitoAgencia: string;
  contaCorrente: string;
  digitoConta: string;
  nomeBeneficiarioCobranca: string;
  apiClientId: string;
  clientSecretConfigurado: boolean;
  apiAmbiente: BanrisulApiAmbiente;
  codigoBeneficiario: string;
  codigoBeneficiarioCompleto: string;
  webhookUrl: string;
  webhookBoletoAtivo: boolean;
  webhookUrlSugerida: string;
  apiMultaPercentual?: number | null;
  apiJurosPercentualMensal?: number | null;
  apiMultaDiasAposVencimento?: number | null;
}

export interface SalvarBanrisulApiConfigPayload {
  descricao: string;
  agencia: string;
  digitoAgencia: string;
  contaCorrente: string;
  digitoConta: string;
  nomeBeneficiarioCobranca: string;
  apiClientId: string;
  apiClientSecret: string;
  apiAmbiente: BanrisulApiAmbiente;
  codigoBeneficiario: string;
  codigoBeneficiarioCompleto: string;
  webhookUrl: string;
  webhookBoletoAtivo: boolean;
  apiMultaPercentual?: number | null;
  apiJurosPercentualMensal?: number | null;
  apiMultaDiasAposVencimento?: number | null;
}

export interface BanrisulConsultaTituloResultado {
  idContaReceber: number;
  statusBanrisul: string;
  liquidado: boolean;
  atualizado: boolean;
  mensagem: string;
}

export interface BanrisulWebhookLogItem {
  id: number;
  idContaReceber: number | null;
  nossoNumero: string;
  seuNumero: string;
  processado: boolean;
  liquidacaoAplicada: boolean;
  mensagem: string;
  dataHora: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class FinBanrisulApiService {
  constructor(
    private ajax: AjaxService,
    private util: UtilService
  ) {}

  obterConfig(): Observable<BanrisulApiConfigView> {
    return this.ajax.get(`${API}financeiro/BanrisulApi/Config`).pipe(
      map((r: any) => {
        if (r?.hasErrors) {
          return this.vazio();
        }
        const d = this.desempacotar(r);
        if (!d.configurado) {
          return this.vazio();
        }
        const amb = String(d.apiambiente ?? d.apiAmbiente ?? 'H').toUpperCase();
        let apiAmbiente: BanrisulApiAmbiente = 'H';
        if (amb === 'P') {
          apiAmbiente = 'P';
        } else if (amb === 'C') {
          apiAmbiente = 'C';
        }
        return {
          configurado: true,
          descricao: String(d.descricao ?? ''),
          agencia: String(d.agencia ?? ''),
          digitoAgencia: String(d.digitoagencia ?? d.digitoAgencia ?? ''),
          contaCorrente: String(d.contacorrente ?? d.contaCorrente ?? ''),
          digitoConta: String(d.digitoconta ?? d.digitoConta ?? ''),
          nomeBeneficiarioCobranca: String(d.nomebeneficiariocobranca ?? d.nomeBeneficiarioCobranca ?? ''),
          apiClientId: String(d.apiclientid ?? d.apiClientId ?? ''),
          clientSecretConfigurado: Boolean(d.clientsecretconfigurado ?? d.clientSecretConfigurado),
          apiAmbiente,
          codigoBeneficiario: String(d.codigobeneficiario ?? d.codigoBeneficiario ?? ''),
          codigoBeneficiarioCompleto: String(d.codigobeneficiariocompleto ?? d.codigoBeneficiarioCompleto ?? ''),
          webhookUrl: String(d.webhookurl ?? d.webhookUrl ?? ''),
          webhookBoletoAtivo: Boolean(d.webhookboletoativo ?? d.webhookBoletoAtivo ?? true),
          webhookUrlSugerida: String(d.webhookurlsugerida ?? d.webhookUrlSugerida ?? ''),
          apiMultaPercentual: this.parseNumeroOpcional(d.apimultapercentual ?? d.apiMultaPercentual),
          apiJurosPercentualMensal: this.parseNumeroOpcional(d.apijurospercentualmensal ?? d.apiJurosPercentualMensal),
          apiMultaDiasAposVencimento: this.parseInteiroOpcional(d.apimultadiasaposvencimento ?? d.apiMultaDiasAposVencimento),
        };
      })
    );
  }

  salvarConfig(payload: SalvarBanrisulApiConfigPayload): Observable<any> {
    return this.ajax.post(`${API}financeiro/BanrisulApi/Config`, payload);
  }

  testarConexao(payload?: {
    apiClientId?: string;
    apiClientSecret?: string;
    apiAmbiente?: string;
  }): Observable<any> {
    return this.ajax.post(`${API}financeiro/BanrisulApi/TestarConexao`, payload ?? {});
  }

  registrarBoletosAbrirGuia(ids: number[], observacoes?: ObservacaoContaReceberPayload[]): Observable<void> {
    return this.ajax
      .postBlob(`${API}financeiro/BanrisulApi/RegistrarBoletosHtml`, { ids, observacoes: observacoes ?? [] })
      .pipe(switchMap((resp) => from(this.tratarBoletoHtml(resp))));
  }

  consultarBoletos(ids: number[]): Observable<BanrisulConsultaTituloResultado[]> {
    return this.ajax.post(`${API}financeiro/BanrisulApi/ConsultarBoletos`, { ids }).pipe(
      map((r: any) => {
        if (r?.hasErrors) {
          throw new Error(r.message || 'Falha na consulta.');
        }
        const d = this.desempacotar(r);
        const titulos = d.titulos || d.Titulos || [];
        return (Array.isArray(titulos) ? titulos : []).map((t: any) => ({
          idContaReceber: Number(t.idcontareceber ?? t.idContaReceber ?? 0),
          statusBanrisul: String(t.statusbanrisul ?? t.statusBanrisul ?? ''),
          liquidado: Boolean(t.liquidado),
          atualizado: Boolean(t.atualizado),
          mensagem: String(t.mensagem ?? ''),
        }));
      })
    );
  }

  consultarExemploLiquidado(): Observable<any> {
    return this.ajax.post(`${API}financeiro/BanrisulApi/ConsultarExemploLiquidado`, {});
  }

  testarWebhook(urlDestino?: string): Observable<any> {
    return this.ajax.post(`${API}financeiro/BanrisulApi/TestarWebhook`, {
      urlDestino: urlDestino || '',
    });
  }

  listarWebhookLogs(limite = 30): Observable<BanrisulWebhookLogItem[]> {
    return this.ajax.get(`${API}financeiro/BanrisulApi/WebhookLogs?limite=${limite}`).pipe(
      map((r: any) => {
        if (r?.hasErrors) {
          return [];
        }
        const d = this.desempacotar(r);
        const logs = d.logs || d.Logs || [];
        return (Array.isArray(logs) ? logs : []).map((x: any) => ({
          id: Number(x.id ?? 0),
          idContaReceber: x.idcontareceber != null ? Number(x.idcontareceber) : null,
          nossoNumero: String(x.nossonumero ?? x.nossoNumero ?? ''),
          seuNumero: String(x.seunumero ?? x.seuNumero ?? ''),
          processado: Boolean(x.processado),
          liquidacaoAplicada: Boolean(x.liquidacaoaplicada ?? x.liquidacaoAplicada),
          mensagem: String(x.mensagem ?? ''),
          dataHora: x.datahora ?? x.dataHora ?? null,
        }));
      })
    );
  }

  private async tratarBoletoHtml(resp: HttpResponse<Blob>): Promise<void> {
    const ct = (resp.headers.get('content-type') || '').toLowerCase();
    const blob = resp.body;
    if (!blob) {
      throw new Error('Resposta vazia do servidor.');
    }
    if (ct.includes('application/json')) {
      const texto = await this.blobParaTexto(blob);
      let j: any;
      try {
        j = JSON.parse(texto);
      } catch {
        throw new Error('Resposta inválida do servidor.');
      }
      if (j.hasErrors) {
        throw new Error(j.message || 'Não foi possível registrar o boleto.');
      }
      return;
    }

    const url = URL.createObjectURL(blob);
    const janela = window.open(url, '_blank', 'noopener');
    if (!janela) {
      URL.revokeObjectURL(url);
      throw new Error('O navegador bloqueou a nova guia. Permita pop-ups para este site e tente de novo.');
    }
    setTimeout(() => URL.revokeObjectURL(url), 120000);
  }

  private blobParaTexto(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result ?? ''));
      fr.onerror = () => reject(fr.error ?? new Error('Falha ao ler resposta.'));
      fr.readAsText(blob);
    });
  }

  private vazio(): BanrisulApiConfigView {
    return {
      configurado: false,
      descricao: 'Banrisul',
      agencia: '',
      digitoAgencia: '',
      contaCorrente: '',
      digitoConta: '',
      nomeBeneficiarioCobranca: '',
      apiClientId: '',
      clientSecretConfigurado: false,
      apiAmbiente: 'H',
      codigoBeneficiario: '',
      codigoBeneficiarioCompleto: '',
      webhookUrl: '',
      webhookBoletoAtivo: true,
      webhookUrlSugerida: '',
      apiMultaPercentual: null,
      apiJurosPercentualMensal: null,
      apiMultaDiasAposVencimento: null,
    };
  }

  private parseNumeroOpcional(valor: unknown): number | null {
    if (valor == null || valor === '') {
      return null;
    }
    const n = Number(valor);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  private parseInteiroOpcional(valor: unknown): number | null {
    if (valor == null || valor === '') {
      return null;
    }
    const n = parseInt(String(valor), 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }

  private desempacotar(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }
}
